import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "app/lib/store/store";
import {
  nextStep,
  prevStep,
  addStepsData,
} from "app/lib/store/slices/steps/jointemploymentDetails";
import { EMPLOYMENT } from "app/constants/apiEndPoints";
import { useEmployment } from "app/hooks/employment/useEmployment";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import MainTitle from "app/components/common/typography/MainTitle";
import ContinueButton from "app/components/common/buttons/Continue";
import PreviousButtom from "app/components/common/buttons/Previous";
import ProgressBar from "app/components/common/progressbar/ProgressBar";
import InfoTitle from "app/components/common/typography/InfoTitle";
import { JointemploymentDetailsFromThree } from "app/utils/types";
import DisabledPreviousEmployerDetails from "./DisabledPreviousEmployerDetails";
import DynamicPreviousEmployer from "app/components/Individual/employmentDetails/dynamicForms/DynamicPreviousEmployer";

const PreviousEmployer: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { mutateAsync: submitEmployment, isPending } = useEmployment();
  const savedIds = useSelector(
    (state: RootState) => state.loanTypesSteps.savedIds
  );
  const { formThree, ...others } = useSelector(
    (state: RootState) => state.jointemploymentDetails.stepsData
  );
  const formTwoData = others?.formTwo;
  const { coBorrowerDetails, yourDetails } = formTwoData || {};

  const isYourPreviousEmployer = yourDetails?.previousemployer === "yes";
  const isCoBorrowerPreviousEmployer =
    coBorrowerDetails?.previousemployer === "yes";

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    setError,
    register,
    clearErrors,
    watch,
    getValues,
  } = useForm<JointemploymentDetailsFromThree>({
    defaultValues: {
      yourDetails: {
        secondjob: "no",
      },
      coBorrowerDetails: {
        secondjob: "no",
      },
    },
  });

  const onSubmit = async (data: JointemploymentDetailsFromThree) => {
    const { yourDetails, coBorrowerDetails } = data || {};

    const yourDetailsData: Partial<any> = {
      ...others?.formTwo?.yourDetails,
      joiningDate: others?.formTwo?.yourDetails?.joiningDate,
      employerName: others?.formOne?.yourDetails?.employerName ?? "",
      previousEmployer: {
        ...yourDetails,
        startDate: yourDetails?.startDate,
        endDate: yourDetails?.endDate,
      },
    };
    const coBorrowerDetailsData: Partial<any> = {
      ...others?.formTwo?.yourDetails,
      joiningDate: others?.formTwo?.coBorrowerDetails?.joiningDate,
      employerName: others?.formOne?.coBorrowerDetails?.employerName ?? "",
      previousEmployer: {
        ...coBorrowerDetails,
        startDate: coBorrowerDetails?.startDate,
        endDate: coBorrowerDetails?.endDate,
      },
    };

    const savedYourDetailsData = {
      ...yourDetails,
      ...(yourDetails?.startDate && {
        startDate: yourDetails.startDate,
      }),
      ...(yourDetails?.endDate && {
        endDate: yourDetails.endDate,
      }),
    };
    const savedCoBorrowerDetailsData = {
      ...coBorrowerDetails,
      ...(coBorrowerDetails?.startDate && {
        startDate: coBorrowerDetails.startDate,
      }),
      ...(coBorrowerDetails?.endDate && {
        endDate: coBorrowerDetails.endDate,
      }),
    };
    const storedData = {
      yourDetails: savedYourDetailsData ?? {},
      coBorrowerDetails: savedCoBorrowerDetailsData ?? {},
    };

    if ("secondjob" in yourDetailsData.previousEmployer) {
      delete yourDetailsData.previousEmployer.secondjob;
    }
    if ("hassecondjob" in yourDetailsData) {
      delete yourDetailsData.hassecondjob;
    }
    if ("previousemployer" in yourDetailsData) {
      delete yourDetailsData.previousemployer;
    }
    if ("secondjob" in coBorrowerDetailsData.previousEmployer) {
      delete coBorrowerDetailsData.previousEmployer.secondjob;
    }
    if ("previousemployer" in coBorrowerDetailsData) {
      delete coBorrowerDetailsData.previousemployer;
    }
    if ("hassecondjob" in coBorrowerDetailsData) {
      delete coBorrowerDetailsData.hassecondjob;
    }

    const request1 = {
      url: `${EMPLOYMENT}/${savedIds?.employmentID}/second`,
      data: yourDetailsData,
    };
    const request2 = {
      url: `${EMPLOYMENT}/${savedIds?.secondEmploymentID}/second`,
      data: coBorrowerDetailsData,
    };

    if (
      yourDetails?.secondjob === "no" &&
      coBorrowerDetails?.secondjob === "no" &&
      isCoBorrowerPreviousEmployer &&
      isYourPreviousEmployer
    ) {
      try {
        const [response1, response2] = await Promise.all([
          submitEmployment(request1),
          submitEmployment(request2),
        ]);
        if (response1?.success && response2?.success) {
          navigate(`/joint-income-details`);
        }
      } catch (error: any) {
        toast.error(error?.response?.data?.message || "Something went wrong.");
      }
    } else if (
      isYourPreviousEmployer &&
      !isCoBorrowerPreviousEmployer &&
      yourDetails?.secondjob === "no"
    ) {
      try {
        const [response1] = await Promise.all([submitEmployment(request1)]);
        if (response1?.success) {
          navigate(`/joint-income-details`);
        }
      } catch (error: any) {
        toast.error(error?.response?.data?.message || "Something went wrong.");
      }
    } else if (
      !isYourPreviousEmployer &&
      isCoBorrowerPreviousEmployer &&
      coBorrowerDetails?.secondjob === "no"
    ) {
      try {
        const [response2] = await Promise.all([submitEmployment(request2)]);
        if (response2?.success) {
          navigate(`/joint-income-details`);
        }
      } catch (error: any) {
        toast.error(error?.response?.data?.message || "Something went wrong.");
      }
    } else if (
      (yourDetails?.secondjob === "yes" &&
        coBorrowerDetails?.secondjob === "no") ||
      (yourDetails?.secondjob === "no" &&
        coBorrowerDetails?.secondjob === "yes")
    ) {
      dispatch(nextStep());
    } else {
      dispatch(nextStep());
    }
    dispatch(
      addStepsData({
        ...others,
        formThree: storedData,
      })
    );
  };

  useEffect(() => {
    if (formThree) {
      reset(formThree);
    }
  }, [formThree, reset]);

  return (
    <>
      <ProgressBar progress={75} MB={4} />
      <div className="w-full sm:w-[60%] lg:w-[40%] mx-auto text-center px-4 sm:px-0">
        <MainTitle title="Previous Employer Details" />
      </div>
      <div className="mx-auto w-internalWidth">
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="flex mb-5 flex-col md:flex-col xl:flex-row gap-3 sm:gap-4 ">
            <div className="flex-1">
              <InfoTitle title="Your Details" />
              {isYourPreviousEmployer && (
                <DynamicPreviousEmployer
                  control={control}
                  errors={errors}
                  register={register}
                  setValue={setValue}
                  setError={setError}
                  clearErrors={clearErrors}
                  jobTitle="yourDetails.jobTitle"
                  employerName="yourDetails.employerName"
                  startDate="yourDetails.startDate"
                  startDateValue={watch("yourDetails.startDate")}
                  secondjob="yourDetails.secondjob"
                  addressStreetName="yourDetails.street"
                  addressStateName="yourDetails.state"
                  addressPostCodeName="yourDetails.postCode"
                  getValues={getValues}
                  endDate="yourDetails.endDate"
                />
              )}

              {!isYourPreviousEmployer && (
                <div className="text-center w-full flex justify-center z-50 align-center relative">
                  <DisabledPreviousEmployerDetails />
                </div>
              )}
            </div>
            <div className="flex-1 ">
              <InfoTitle title="Co-borrower" />
              {isCoBorrowerPreviousEmployer && (
                <DynamicPreviousEmployer
                  control={control}
                  errors={errors}
                  register={register}
                  setValue={setValue}
                  setError={setError}
                  clearErrors={clearErrors}
                  jobTitle="coBorrowerDetails.jobTitle"
                  employerName="coBorrowerDetails.employerName"
                  startDate="coBorrowerDetails.startDate"
                  endDate="coBorrowerDetails.endDate"
                  startDateValue={watch("coBorrowerDetails.startDate")}
                  secondjob="coBorrowerDetails.secondjob"
                  addressStreetName="coBorrowerDetails.street"
                  addressStateName="coBorrowerDetails.state"
                  addressPostCodeName="coBorrowerDetails.postCode"
                  getValues={getValues}
                />
              )}
              {!isCoBorrowerPreviousEmployer && (
                <div className="text-center w-full flex justify-center z-50  align-center relative">
                  <DisabledPreviousEmployerDetails />
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-between mb-6">
            <PreviousButtom
              onClick={() => {
                dispatch(prevStep());
              }}
            >
              Previous
            </PreviousButtom>
            <ContinueButton type="submit" loading={isPending}>
              Next
            </ContinueButton>
          </div>
        </form>
      </div>
    </>
  );
};

export default PreviousEmployer;
